import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiX, FiPlus } from 'react-icons/fi';
import { getAvailableUsers, mapUserToSolution, unmapUserFromSolution } from 'api/solutions';
import { useOrganization } from 'hooks/OrganizationContext';

interface UserMapping {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: string;
}

interface AvailableUser {
  id: string;
  name: string;
  email: string;
}

interface MapNewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onUserMapped: () => void; // Callback to refresh user list
}

const MapNewUserModal: React.FC<MapNewUserModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onUserMapped,
}) => {
  const [userMappings, setUserMappings] = useState<UserMapping[]>([
    { id: '1', userId: '', userName: '', userEmail: '', role: '' }
  ]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const { selectedOrganization } = useOrganization();

  const roles = [
    { value: 'OWNER', label: 'Owner' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'DEVELOPER', label: 'Developer' },
    { value: 'DESIGNER', label: 'Designer' },
    { value: 'USER', label: 'User' }
  ];

  // Fetch available users when modal opens
  useEffect(() => {
    if (isOpen && selectedOrganization?.id && projectId) {
      fetchAvailableUsers();
    }
  }, [isOpen, selectedOrganization?.id, projectId]);

  const fetchAvailableUsers = async () => {
    if (!selectedOrganization?.id || !projectId) return;
    
    setIsFetchingUsers(true);
    try {
      const response = await getAvailableUsers(selectedOrganization.id, projectId);
      if (response.status === 200) {
        setAvailableUsers(response.data.list || []);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      setAvailableUsers([]);
    } finally {
      setIsFetchingUsers(false);
    }
  };

  const addNewRow = () => {
    const newId = (userMappings.length + 1).toString();
    setUserMappings([...userMappings, { id: newId, userId: '', userName: '', userEmail: '', role: '' }]);
  };

  const removeRow = (id: string) => {
    if (userMappings.length > 1) {
      setUserMappings(userMappings.filter(user => user.id !== id));
    }
  };

  const updateUserMapping = (id: string, field: keyof UserMapping, value: string) => {
    setUserMappings(userMappings.map(user => {
      if (user.id === id) {
        const updatedUser = { ...user, [field]: value };
        
        // If userId changed, update userName and userEmail
        if (field === 'userId') {
          const selectedUser = availableUsers.find(au => au.id === value);
          if (selectedUser) {
            updatedUser.userName = selectedUser.name;
            updatedUser.userEmail = selectedUser.email;
          }
        }
        
        return updatedUser;
      }
      return user;
    }));
  };

  const handleMapUsers = async () => {
    const validMappings = userMappings.filter(user => user.userId && user.role);
    if (validMappings.length === 0) {
      return;
    }

    setIsLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Map users one by one
      for (const mapping of validMappings) {
        try {
          const response = await mapUserToSolution(selectedOrganization!.id, projectId, {
            userId: mapping.userId,
            role: mapping.role
          });
          
          if (response.status === 200) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        // Close modal and refresh user list
        onUserMapped();
        onClose();
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form
    setUserMappings([{ id: '1', userId: '', userName: '', userEmail: '', role: '' }]);
  };

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-md h-[80vh]">
          <div className="bg-[#1A1A1A] rounded-lg border border-white/[0.08] shadow-lg flex flex-col h-full">
            {/* Header - Fixed height */}
            <div className="flex items-center justify-between p-6 border-b border-white/[0.08] flex-shrink-0">
              <h2 className="text-lg font-semibold text-white">
                Map New User
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Content - Scrollable with fixed height */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <div className="space-y-4">
                {isFetchingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    <span className="ml-3 text-white">Loading available users...</span>
                  </div>
                ) : availableUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No users available for mapping
                  </div>
                ) : (
                  <>
                    {userMappings.map((user, index) => (
                      <div key={user.id} className="space-y-3 p-3 border border-white/[0.08] rounded-md">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">User #{index + 1}</span>
                          {userMappings.length > 1 && (
                            <button
                              onClick={() => removeRow(user.id)}
                              className="text-red-400 hover:text-red-300 p-1"
                            >
                              <FiX size={16} />
                            </button>
                          )}
                        </div>
                        
                        {/* User Selection */}
                        <div>
                          <select
                            value={user.userId}
                            onChange={(e) => updateUserMapping(user.id, 'userId', e.target.value)}
                            className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                          >
                            <option value="">Select User</option>
                            {availableUsers.map(availableUser => (
                              <option key={availableUser.id} value={availableUser.id}>
                                {availableUser.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Role Selection */}
                        <div>
                          <select
                            value={user.role}
                            onChange={(e) => updateUserMapping(user.id, 'role', e.target.value)}
                            className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                          >
                            <option value="">Select Role</option>
                            {roles.map(role => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}

                    {/* Add More Button */}
                    <button
                      onClick={addNewRow}
                      className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors text-sm"
                    >
                      <div className="w-6 h-6 rounded-full bg-green-400/20 flex items-center justify-center">
                        <FiPlus size={14} className="text-green-400" />
                      </div>
                      Add More
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Footer - Fixed position with buttons */}
            <div className="flex justify-end p-6 border-t border-white/[0.08] flex-shrink-0">
              <button
                onClick={handleMapUsers}
                disabled={isLoading || availableUsers.length === 0}
                className={`px-6 py-2 rounded-md transition-colors font-medium ${
                  isLoading || availableUsers.length === 0
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-transparent border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Mapping...
                  </div>
                ) : (
                  'MAP USERS'
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default MapNewUserModal; 